import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { t } from 'i18next';
import { createContext, ReactNode, useContext, useState } from 'react';
import { PageLoader } from '~components/ui/PageLoader';
import { defaultPageSizes, PageSizeSelector } from '~components/ui/PageSizeSelector';
import { PaginationServerSide } from '~components/ui/PaginationServerSide';

export const SEARCH_PAGE_SIZES = defaultPageSizes;

export interface SearchResultColumn<TRow> {
  id: string;
  header: string;
  width?: string;
  render: (row: TRow) => ReactNode;
}

export interface SearchPagination {
  pageIndex: number;
  pageSize: number;
  onPageIndexChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const SearchPageSizeContext = createContext<{
  pageSize: number;
  setPageSize: (pageSize: number) => void;
}>({ pageSize: SEARCH_PAGE_SIZES[0], setPageSize: () => undefined });

export function useSearchPagination(): SearchPagination {
  const { pageSize, setPageSize } = useContext(SearchPageSizeContext);
  const [pageIndex, setPageIndex] = useState(0);

  return {
    pageIndex,
    pageSize,
    onPageIndexChange: setPageIndex,
    onPageSizeChange: (size) => {
      setPageSize(size);
      setPageIndex(0);
    },
  };
}

interface Props<TRow extends { id: string }> {
  isLoading: boolean;
  rows: TRow[];
  totalCount: number;
  columns: Array<SearchResultColumn<TRow>>;
  pagination: SearchPagination;
}

export function SearchResultsTable<TRow extends { id: string }>({
  isLoading,
  rows,
  totalCount,
  columns,
  pagination: { pageIndex, pageSize, onPageIndexChange, onPageSizeChange },
}: Props<TRow>) {
  const pageCount = Math.ceil(totalCount / pageSize);

  return (
    <>
      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
      >
        {isLoading ? (
          <Box padding="8">
            <PageLoader />
          </Box>
        ) : (
          <Table variant="simple" width="100%">
            <Thead bgColor="gray.50">
              <Tr>
                {columns.map((column) => (
                  <Th key={column.id} width={column.width} color="gray.700" whiteSpace="nowrap">
                    {column.header}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center" color="gray.500">
                    {t('adminSearchNoResults')}
                  </Td>
                </Tr>
              ) : (
                rows.map((row) => (
                  <Tr key={row.id}>
                    {columns.map((column) => (
                      <Td key={column.id} whiteSpace="nowrap">
                        {column.render(row)}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      {pageCount > 0 && (
        <Box display="flex" marginTop="4">
          <Box flex="1">
            <PageSizeSelector
              label={t('showRowsPerPage')}
              options={SEARCH_PAGE_SIZES}
              value={pageSize}
              onChange={onPageSizeChange}
            />
          </Box>
          <PaginationServerSide
            pageCount={pageCount}
            currentPageIndex={pageIndex}
            hasPreviousPage={pageIndex > 0}
            hasNextPage={pageIndex < pageCount - 1}
            goToPage={onPageIndexChange}
            goToPreviousPage={() => onPageIndexChange(pageIndex - 1)}
            goToNextPage={() => onPageIndexChange(pageIndex + 1)}
          />
        </Box>
      )}
    </>
  );
}
